import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, ListChecks, Calendar, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api/attendance/biometric';
const API_ROOT = process.env.REACT_APP_BACKEND_URL + '/api';

const ETimeTrackLiteImport = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [file, setFile] = useState(null);
  const [autoProcess, setAutoProcess] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState({ cutoff_date: '2026-04-21' });

  useEffect(() => {
    axios.get(`${API_ROOT}/companies`, { withCredentials: true })
      .then(({ data }) => {
        setCompanies(data);
        setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
      })
      .catch(console.error);
    axios.get(`${API}/settings`, { withCredentials: true })
      .then(({ data }) => setSettings(data))
      .catch(console.error);
  }, [setSelectedCompany]);

  const handleUpload = async () => {
    if (!file) { toast.error('Pick a CSV file first'); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params = new URLSearchParams();
      if (selectedCompany) params.append('company_id', selectedCompany);
      params.append('auto_process', autoProcess);
      const { data } = await axios.post(
        `${API}/etimetracklite/upload?${params}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(data);
      toast.success(`${data.inserted} punches imported${data.auto_processed ? `, ${data.auto_processed.attendance_recalculated || 0} attendance days recalculated` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="etimetracklite-import-page">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            <FileSpreadsheet className="inline-block w-8 h-8 mr-2 mb-1" /> eTimeTrackLite Daily Import
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Upload the daily CSV export from eTimeTrackLite Server — punches are mapped to employees and attendance is recalculated automatically.
          </p>
        </div>

        {/* Cutoff banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3" data-testid="cutoff-banner">
          <Calendar className="w-5 h-5 text-blue-600" />
          <div className="flex-1">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Effective date: {settings.cutoff_date}</span> — punches before this date are skipped (set in Device Mapping → Settings).
            </p>
          </div>
        </div>

        {/* Upload card */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              data-testid="company-select"
              className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm w-full max-w-sm"
            >
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">CSV Export from eTimeTrackLite</label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              data-testid="etl-file-input"
              className="block w-full text-sm border border-slate-300 rounded-md p-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              <strong>Accepted formats (auto-detected):</strong><br />
              • <strong>Matrix / Pivot</strong> — the default eTimeTrackLite Attendance Report (one row per employee, each date as a column with values like <code className="bg-slate-100 px-1 rounded">09:00-18:00(P)(OD~RA)</code>).<br />
              • <strong>Tall punch</strong> — columns <code className="bg-slate-100 px-1 rounded">Employee Code</code>, <code className="bg-slate-100 px-1 rounded">Date</code>, <code className="bg-slate-100 px-1 rounded">InTime</code>, <code className="bg-slate-100 px-1 rounded">OutTime</code>.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={autoProcess}
              onChange={(e) => setAutoProcess(e.target.checked)}
              data-testid="auto-process-toggle"
            />
            Auto-process after upload (resolve mappings + recalculate attendance)
          </label>
          <div className="pt-2">
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              data-testid="etl-upload-btn"
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Import Daily Punches
            </Button>
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="etl-result">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">
                {result.format === 'matrix' ? 'Matrix (pivot) format detected' : 'Daily punch format detected'} —{' '}
                {result.inserted} raw punches + {result.attendance_records_written || 0} attendance days imported
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs uppercase text-slate-500 mb-1">Raw Punches Inserted</p>
                <p className="text-lg font-semibold text-slate-900">{result.inserted}</p>
              </div>
              {result.format === 'matrix' && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs uppercase text-slate-500 mb-1">Attendance Days Written</p>
                  <p className="text-lg font-semibold text-slate-900">{result.attendance_records_written || 0}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">includes Absent / WO / Leave / Holiday</p>
                </div>
              )}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs uppercase text-slate-500 mb-1">Skipped (before {result.cutoff_date})</p>
                <p className="text-lg font-semibold text-amber-700">{result.skipped_before_cutoff}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs uppercase text-slate-500 mb-1">Errors</p>
                <p className="text-lg font-semibold text-red-700">{result.error_count}</p>
              </div>
              {result.auto_processed && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs uppercase text-blue-700 mb-1">Attendance days recalc.</p>
                  <p className="text-lg font-semibold text-blue-900">{result.auto_processed.attendance_recalculated || 0}</p>
                </div>
              )}
            </div>
            {(result.skipped_not_mapped > 0 || (result.auto_processed && result.auto_processed.skipped_no_mapping > 0)) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-amber-900">
                    {(result.skipped_not_mapped || 0) + (result.auto_processed?.skipped_no_mapping || 0)} records couldn't be matched to an employee
                  </p>
                  <p className="text-amber-800 text-xs mt-0.5">
                    Some device user-ids / codes aren't yet mapped to employees. Visit{' '}
                    <a href="/attendance/biometric/mapping" className="underline font-medium">Device Mapping</a> to fix.
                  </p>
                </div>
              </div>
            )}
            {result.errors?.length > 0 && (
              <details>
                <summary className="cursor-pointer text-xs text-slate-500">View row-level errors ({result.errors.length})</summary>
                <ul className="text-xs text-red-700 mt-1 space-y-0.5 max-h-48 overflow-auto">
                  {result.errors.map((e, i) => (<li key={i}>Row {e.row}: {e.error}</li>))}
                </ul>
              </details>
            )}
          </div>
        )}

        {/* How-to */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="etl-howto">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-800">How to export from eTimeTrackLite Server</span>
          </div>
          <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1 leading-relaxed">
            <li>Open eTimeTrackLite Server → <span className="font-mono">Reports</span> → <span className="font-mono">Daily Attendance</span>.</li>
            <li>Pick the date range (typically <em>yesterday</em> for daily ops).</li>
            <li>Export → choose <span className="font-mono">CSV</span> format. Save the file.</li>
            <li>Upload the CSV here. The system auto-resolves device user-ids → employees and updates attendance.</li>
            <li>For real-time daily integration, install the <a href="/attendance/biometric" className="underline font-medium text-[#0052FF]">Local Gateway Agent</a> instead — it polls the device every 10 minutes and pushes punches automatically.</li>
          </ol>
        </div>
      </div>
    </MainLayout>
  );
};

export default ETimeTrackLiteImport;
